import React, { memo, useContext } from "react";
import { ThreadListItemProps } from "../../../types/ruleBasedInbox";
import {
    THREAD_LIST_COLORS,
    RECENT_CHAT_LABEL,
    TICKET_STATUS_TYPES
} from "../constants";
import styles from "../../../assets/styles/pageStyles/ruleBasedInbox/threadList/threadList.module.scss";
import GlobalTooltip from "../../../components/utility/tooltip/globalTooltip";
import ArchiveIcon from "@mui/icons-material/Archive";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import { CHRONOX_FEATURES, getIntegrationIcon, isFeatureDisabled } from "../../../utils/constants";
import { isInstagramChannel, isWhatsAppChannel } from "../channelConstants";
import { UserContext, UserContextPayload } from "../../../context/userContext";
import ReactCountryFlag from "react-country-flag";
import { formatPhoneNumber, formatThreadTime, getFormattedLastMessage, getThreadChatbotUserCountry, getThreadChatbotUserCountryCode, getThreadDisplayName, getThreadPhoneNumber, getThreadInstagramUserName, isChannelNameWebsiteOrChronox } from "../utils";
import { renderAllAssignedAgentAvatar } from "../renderUtils";
interface ThreadListItemComponentProps extends ThreadListItemProps {
    style?: React.CSSProperties;
    hasUnreadMessages?: boolean;
    isRecent?: boolean; // If true, show "Recent" instead of timestamp
    getWabaNameAndPhoneNumberBytIntegratedChannelId?: (
        integrated_channel_name: string,
        integrated_channel_id: number
    ) => { waba_name: string; phone_number: string; phone_number_id: string } | null;
    getInstaAccountNameAndIdByIntegratedChannelId?: (
        integrated_channel_name: string,
        integrated_channel_id: number
    ) => { instagram_account_name: string; instagram_account_id: string } | null;
}

/**
 * Memoized Thread List Item Component
 * Renders individual thread item in the conversation list
 */
const ThreadListItem = memo(
    ({ thread, isSelected, onThreadClick, style, hasUnreadMessages, isRecent, getWabaNameAndPhoneNumberBytIntegratedChannelId, getInstaAccountNameAndIdByIntegratedChannelId }: ThreadListItemComponentProps) => {
        
        const { userDetail } = useContext(UserContext) as UserContextPayload

        const isWhatsapp = isWhatsAppChannel(thread.channel_name);
        const isInstagram = isInstagramChannel(thread.channel_name);
        
        const displayName = getThreadDisplayName(thread.thread_metadata);
        const phoneNumber = getThreadPhoneNumber(thread.thread_metadata);
        
        const userName = getThreadInstagramUserName(thread.thread_metadata);

        // Get Instagram Account Info
        const instagramInfo = isInstagram && getInstaAccountNameAndIdByIntegratedChannelId
            ? getInstaAccountNameAndIdByIntegratedChannelId(thread.channel_name, thread.integrated_channel_id)
            : null;
        const instaAccountName = instagramInfo?.instagram_account_name || "";
        const instaAccountId = instagramInfo?.instagram_account_id || "";

        const chatbotUserCountry = getThreadChatbotUserCountry(thread?.thread_metadata);
        const chatbotUserCountryCode = getThreadChatbotUserCountryCode(thread?.thread_metadata);
        
        // Get business information
        const businessInfo = getWabaNameAndPhoneNumberBytIntegratedChannelId
            ? getWabaNameAndPhoneNumberBytIntegratedChannelId(thread.channel_name, thread.integrated_channel_id)
            : null;
        const businessName = businessInfo?.waba_name || "";
        const businessPhoneNumber = businessInfo?.phone_number || "";
        // Get actual formatted time for tooltip
        const actualFormattedTime = formatThreadTime(thread.thread_last_activity_inbox_criteria, userDetail?.timezone_hour ?? null);
        // Show "Recent" label instead of timestamp if thread is marked as recent
        const displayTimeText = actualFormattedTime?.displayText;
        const fullDateTimeText = actualFormattedTime?.fullDateTimeText;
        const displayTime = isRecent ? RECENT_CHAT_LABEL : displayTimeText;
        // Get formatted last message - pass displayName as userName for user messages
        const { senderLabel, messageText } = getFormattedLastMessage(
            thread.last_message,
            userDetail?.user_assignment_id ?? null,
            displayName // User's name from thread metadata
        );
  
        const assignedAgents = thread?.manual_agent_data ?? [];

        // Get ticket status and color
        const hasTicket = thread?.thread_ticket_data?.length > 0;
        const ticketWithOpenStatus = thread?.thread_ticket_data?.find((ticket) => {
            const hasOpenStatus = (ticket?.status_assignments?.find((status) => !status?.flag_substatus)?.ticket_status_name ?? '') === TICKET_STATUS_TYPES?.OPEN;
            if(hasOpenStatus){
                return true;
            }
            return false;
        })
        
        const getTicketColor = () => {
            if(ticketWithOpenStatus) return "#1976D2";
            else return "#4CAF50";
        };
        const ticketColor = getTicketColor();
        const ticketStatusName = ticketWithOpenStatus ? TICKET_STATUS_TYPES?.OPEN : TICKET_STATUS_TYPES?.RESOLVED

        // Check for highlight (new message received)
        const isHighlighted = thread.flag_highlight_thread || thread.flag_new_messages;

        // Show unread indicator when thread has unread messages (from socket) and is not selected
        const showUnreadIndicator = hasUnreadMessages && !isSelected;

        // Render info row for WhatsApp and Instagram
        const renderInfoRow = (primaryText: string, businessLabel: string, tooltipSubText: string) => {
            
            if (!primaryText && !businessLabel) return null;
        
            return (
                <div className={styles.threadPhoneNumberRow}>
                    {primaryText && (
                        <span className={styles.threadPhoneNumber}>{primaryText}</span>
                    )}

                    {primaryText && businessLabel && (
                        <span className={styles.threadPhoneNumberSeparator}>•</span>
                    )}

                    {businessLabel && (
                        <GlobalTooltip
                            title={
                                <div>
                                    <div style={{ fontWeight: 500, marginBottom: tooltipSubText ? 4 : 0 }}>{businessLabel}</div>
                                    {tooltipSubText && (
                                        <div style={{ fontSize: 12, opacity: 0.9 }}>{tooltipSubText}</div>
                                    )}
                                </div>
                            }
                            placement="top"
                            arrow
                        >
                            <span className={styles.threadBusinessName}>{businessLabel}</span>
                        </GlobalTooltip>
                    )}
                </div>
            );
        };

        return (
            <div
                style={style}
                className={`${styles.threadListItem} ${isSelected ? styles.selected : ""} ${isHighlighted ? styles.highlighted : ""}`}
                onClick={() => onThreadClick(thread)}
            >
                {/* WhatsApp Icon Section */}
                <div className={styles.threadAvatar}>
                    {getIntegrationIcon(thread?.channel_name, "18", styles.whatsappIcon)}
                </div>

                {/* Content Section */}
                <div className={styles.threadContent}>
                    {/* Top Row: Name and Time with Agent */}
                    <div className={styles.threadTopRow}>
                        <div className={styles.threadNameContainer}>
                            {/* Unread Indicator - positioned next to name */}
                            {showUnreadIndicator && (
                                <span className={styles.unreadIndicator} />
                            )}
                            <span className={styles.threadName}>{displayName}</span>
                        </div>
                        <div className={styles.threadTimeContainer}>
                            {(chatbotUserCountry && isChannelNameWebsiteOrChronox(thread?.channel_name)) &&
                                <ReactCountryFlag
                                    countryCode={chatbotUserCountryCode}
                                    svg
                                    title={chatbotUserCountry}
                                />
                            }
                            
                            <GlobalTooltip title={isRecent ? displayTimeText : fullDateTimeText} placement="top" arrow>
                                <span className={styles.threadTime}>{displayTime}</span>
                            </GlobalTooltip>
                            
                            {/* Ticket Status Icon */}
                            {(hasTicket && !isFeatureDisabled(userDetail, CHRONOX_FEATURES['THREAD_TICKETING'])) && (
                                <GlobalTooltip
                                    title={`Ticket: ${ticketStatusName}`}
                                    placement="top"
                                    arrow
                                >
                                    <ConfirmationNumberOutlinedIcon
                                        style={{
                                            fontSize: 16,
                                            color: ticketColor,
                                            marginLeft: 4,
                                        }}
                                    />
                                </GlobalTooltip>
                            )}

                            {/* Agent Avatar Circle */}
                            {assignedAgents.length > 0 && 
                                renderAllAssignedAgentAvatar(assignedAgents)
                            }
                            
                            {/* Archived Icon */}
                            {thread.flag_archive && (
                                <GlobalTooltip
                                    title="This chat has been Archived"
                                    placement="top"
                                    arrow
                                >
                                    <ArchiveIcon
                                        style={{
                                            fontSize: 16,
                                            color: THREAD_LIST_COLORS.ARCHIVE_ICON,
                                            marginLeft: 4,
                                        }}
                                    />
                                </GlobalTooltip>
                            )}
                        </div>
                    </div>

                    {/* Render Info Row */}
                    {isWhatsapp && renderInfoRow(
                        phoneNumber && displayName !== phoneNumber ? formatPhoneNumber(phoneNumber) : "",
                        businessName,
                        businessPhoneNumber
                    )}

                    {isInstagram && renderInfoRow(
                        userName,
                        instaAccountName,
                        instaAccountId
                    )}

                    {/* Last Message Preview */}
                    <span className={styles.threadLastMessage}>
                        {senderLabel && <span className={styles.senderLabel}>{senderLabel}</span>}
                        {messageText}
                    </span>
                </div>
            </div>
        );
    },
    // Custom comparison function for memo

    (prevProps, nextProps) => {

        return (
            prevProps.thread.thread_id === nextProps.thread.thread_id &&
            prevProps.isSelected === nextProps.isSelected &&
            prevProps.hasUnreadMessages === nextProps.hasUnreadMessages &&
            prevProps.isRecent === nextProps.isRecent &&
            prevProps.thread.thread_last_activity_inbox_criteria ===
            nextProps.thread.thread_last_activity_inbox_criteria &&
            prevProps.thread.manual_agent_data?.length ===
            nextProps.thread.manual_agent_data?.length &&
            prevProps.thread?.thread_ticket_data === 
            nextProps.thread?.thread_ticket_data &&
            prevProps.thread.flag_highlight_thread === nextProps.thread.flag_highlight_thread &&
            prevProps.thread.flag_new_messages === nextProps.thread.flag_new_messages &&
            prevProps.thread.last_message?.thread_last_message_id ===
            nextProps.thread.last_message?.thread_last_message_id &&
            prevProps.thread.flag_archive === nextProps.thread.flag_archive &&
            prevProps.thread.manual_agent_data === nextProps.thread.manual_agent_data &&
            prevProps.thread.channel_name === nextProps.thread.channel_name &&
            prevProps.thread.integrated_channel_id === nextProps.thread.integrated_channel_id
        );
    }
);

ThreadListItem.displayName = "ThreadListItem";

export default ThreadListItem;


